import Input from '../../../../components/Input';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import MultiSelect from '../../../../components/MultiSelect';
import { useController, useFormContext, Controller } from 'react-hook-form';
import { useDebounce } from '@/hooks/useDebounce';
import { useRef } from 'react';
import useFetchFlattenJobSources from '../../api/useFetchJobSources';
import useProvidedJobPostTags from '../../provided_job_posts/hooks/useProvidedJobPostTags';
import useProvidedJobPostLists from '../../provided_job_post_lists/api/useProvidedJobPostLists';
import MultiSelectPopover from '../../provided_job_posts/components/MultiSelectPopover';
import type { FormValue } from '../../provided_job_posts/searchSchema';
import CompanySelect from '../../provided_job_posts/components/CompanySelect';
import JobSourceList from '../../provided_job_posts/components/JobSourceList';
import CustomTagMatchMenu from '@/components/CustomTagMatchMenu';
import MultiSelectManageLink from '@/components/MultiSelectManageLink';
import Select from '@/components/Select';
import usePrefectureSelect from '../../provided_job_posts/hooks/usePrefectureSelect';

// 求人管理ページ用のソートオプション
const orderItems = [
  { value: 'updated_at:desc', label: '更新が新しい順' },
  { value: 'updated_at:asc', label: '更新が古い順' },
  { value: 'last_crawled_at:desc', label: '外部連携が新しい順' },
  { value: 'created_at:desc', label: '作成が新しい順' },
  { value: 'created_at:asc', label: '作成が古い順' },
];

const categoryItems = [
  { value: 'title', label: '求人タイトル' },
  { value: 'mandatory_requirement', label: '必須要件' },
  { value: 'keyword_content', label: '求人内容' },
  { value: 'memo', label: '社内メモ' },
];

const conditionItems = [
  { value: 'or', label: 'のいずれかを含む(OR)' },
  { value: 'and', label: 'を全て含む(AND)' },
  { value: 'not_or', label: 'のいずれかを含まない(NOT/OR)' },
  { value: 'not_and', label: 'をすべて含まない(NOT/AND)' },
];

interface Props {
  onSubmit: (data: FormValue) => void;
}

export default function CandidatesProvidedJobPostModalSearchForm({ onSubmit }: Props) {
  const debounce = useDebounce(500);
  const [searchJobSource, setSearchJobSource] = useState('');
  const [jobSourceName, setJobSourceName] = useState('');
  const [isJobSourceFocus, setIsJobSourceFocus] = useState(false);
  const { data: jobSources } = useFetchFlattenJobSources({ query: searchJobSource });
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { data } = useProvidedJobPostTags();
  const { data: lists } = useProvidedJobPostLists();
  const isInitialMount = useRef(true);
  const searchDebounce = useDebounce(300);

  const { register, watch, handleSubmit, control, setValue } = useFormContext<FormValue>();
  const customTagIds = watch('customTagIds');
  const customTagIdsController = useController({ name: 'customTagIds', control });
  const customTagMatchController = useController({ name: 'customTagMatch', control });
  const listIdController = useController({ name: 'listId', control });
  const sortController = useController({ name: 'sort', control });

  const { prefectureNames, prefectureOptions, handlePrefectureUpdate } = usePrefectureSelect();

  const onSelectJobSource = (id: number, name: string) => {
    setValue('jobSourceId', id);
    setJobSourceName(name);
  };

  const onInputJobSource = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setJobSourceName(value);

    debounce(async () => {
      setSearchJobSource(value);
    });
  };

  // 自動検索機能 - フォームの変更を監視（デバウンス付き）
  useEffect(() => {
    // 初回レンダリング時はスキップ
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const subscription = watch(() => {
      searchDebounce(() => {
        handleSubmit(onSubmit)();
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, onSubmit, searchDebounce]);

  // タグ選択用のオプション
  const tagOptions =
    data?.tags?.map((tag) => ({
      id: tag.id,
      name: tag.name,
      colorHex: tag.colorHex,
    })) ?? [];

  return (
    <>
      <div className="w-full rounded bg-gray p-md">
        <div className="space-y-lg">
          <div className="flex gap-sm">
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">企業名</label>
              <CompanySelect onSetCompanyIds={() => {}} />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">ポジション</label>
              <Input
                search
                placeholder="ポジションで絞り込む"
                {...register('jobPositions')}
                className="w-full"
              />
            </div>

            <div className="flex shrink-0 flex-col gap-xs">
              <label className="text-heading-xxs">年収</label>
              <div className="flex items-center gap-xs whitespace-nowrap">
                <Input {...register('salary')} placeholder="0" className="w-[96px]" />
                <span className="text-body-sm">万円を含む</span>
                <Input {...register('minSalary')} placeholder="0" className="w-[96px]" />
                <span className="text-body-sm">〜</span>
                <Input {...register('maxSalary')} placeholder="0" className="w-[96px]" />
                <span className="text-body-sm">万円</span>
              </div>
            </div>
          </div>

          <div className="flex gap-sm">
            {/* 勤務地検索 */}
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">勤務地</label>
              <MultiSelectPopover
                values={prefectureNames}
                placeholder="勤務地を選択"
                options={prefectureOptions}
                onUpdate={handlePrefectureUpdate}
                searchable
                align="start"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">タグ</label>
              <MultiSelect
                options={tagOptions}
                value={customTagIds}
                onChange={(values) => {
                  customTagIdsController.field.onChange(values);
                }}
                placeholder="タグを選択"
                variant="list"
                header={
                  <CustomTagMatchMenu
                    value={customTagMatchController.field.value}
                    onChange={customTagMatchController.field.onChange}
                  />
                }
                footer={
                  <MultiSelectManageLink to="/agent/job_post_tags">
                    タグを管理
                  </MultiSelectManageLink>
                }
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">リスト</label>
              <Select
                items={
                  lists?.map((list) => ({
                    value: String(list.id),
                    label: list.name,
                  })) ?? []
                }
                value={listIdController.field.value}
                onValueChange={listIdController.field.onChange}
                defaultLabel="リストを選択"
              />
            </div>
          </div>

          {/* 詳細検索アコーディオンボタン */}
          <div className="flex justify-start">
            <Button
              color="tertiary"
              size="md"
              type="button"
              onClick={() => setIsDetailOpen(!isDetailOpen)}
            >
              {isDetailOpen ? (
                <MdKeyboardArrowUp className="size-icon-xs text-primary" />
              ) : (
                <MdKeyboardArrowDown className="size-icon-xs text-primary" />
              )}
              <span className="text-sm font-bold text-primary">
                {isDetailOpen ? '詳細条件を閉じる' : '詳細条件を開く'}
              </span>
            </Button>
          </div>

          {/* 詳細検索アコーディオンコンテンツ */}
          {isDetailOpen && (
            <div className="space-y-lg">
              {/* 1段目: 取り込み元で絞り込む */}
              <div className="flex flex-col gap-xs">
                <label className="text-heading-xxs">取り込み元で絞り込む</label>
                <div className="relative w-full lg:w-[417px]">
                  <Input
                    className="w-full"
                    placeholder="連携先を入力"
                    autoComplete="off"
                    onChange={onInputJobSource}
                    value={jobSourceName}
                    onFocus={() => setIsJobSourceFocus(true)}
                    onBlur={() => setTimeout(() => setIsJobSourceFocus(false), 200)}
                  />
                  {isJobSourceFocus && (
                    <div className="absolute top-11 z-50">
                      <JobSourceList
                        lists={jobSources ?? []}
                        onSelectJobSource={onSelectJobSource}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 2段目: 詳細キーワード設定 */}
              <div className="flex flex-col gap-sm">
                <label className="text-heading-xxs">詳細キーワード設定で絞り込む!</label>

                {/* Alpha検索 */}
                <div className="flex items-center gap-xs">
                  <div className="w-[200px]">
                    <Controller
                      name="searchColumnAlpha"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-body-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordAlpha')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorAlpha"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Beta検索 */}
                <div className="flex items-center gap-xs">
                  <div className="w-[200px]">
                    <Controller
                      name="searchColumnBeta"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-body-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordBeta')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorBeta"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Gamma検索 */}
                <div className="flex items-center gap-xs">
                  <div className="w-[200px]">
                    <Controller
                      name="searchColumnGamma"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-body-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordGamma')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorGamma"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ソート機能 */}
      <div className="mt-sm flex items-end justify-between gap-4">
        <div className="flex w-[196px] shrink-0 flex-col gap-xs">
          <Select
            className="w-full"
            value={sortController.field.value}
            onValueChange={sortController.field.onChange}
            items={orderItems}
          />
        </div>
      </div>
    </>
  );
}
